import React, { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Shield, ShieldCheck, ShieldAlert, Activity, Lock, FileWarning } from "lucide-react";

const SEV_STYLES = {
  info: "text-slate-600 bg-slate-50 border-slate-200",
  ok: "text-emerald-800 bg-emerald-50 border-emerald-200",
  warn: "text-amber-800 bg-amber-50 border-amber-200",
};

export default function Compliance() {
  const [events, setEvents] = useState([]);
  const { user } = useAuth();

  useEffect(() => {
    if (!user) return;
    http.get("/compliance/audit").then((r) => setEvents(r.data.events)).catch(() => {});
  }, [user]);

  const stats = [
    { Icon: Shield, label: "HIPAA-aware", value: "Active", color: "sky" },
    { Icon: Lock, label: "Encryption", value: "AES-256 / TLS 1.3", color: "emerald" },
    { Icon: ShieldCheck, label: "BAA (demo)", value: "Placeholder", color: "emerald" },
    { Icon: FileWarning, label: "PHI Detection", value: "Enabled", color: "amber" },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-12 lg:py-16" data-testid="compliance-ui-panel">
      <div>
        <p className="text-xs font-mono uppercase tracking-widest text-sky-700">Compliance</p>
        <h1 className="mt-2 font-display text-3xl lg:text-5xl font-bold text-slate-900">
          HIPAA-aware infrastructure signals.
        </h1>
        <p className="mt-3 text-slate-600 max-w-2xl">
          This dashboard summarizes compliance signals for your workspace. Placeholders in demo mode.
        </p>
      </div>

      <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map(({ Icon, label, value }) => (
          <div key={label} className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={`compliance-stat-${label.replace(/\s+/g,'-').toLowerCase()}`}>
            <Icon className="h-5 w-5 text-emerald-800" />
            <p className="mt-3 text-xs text-slate-500 uppercase tracking-wide font-mono">{label}</p>
            <p className="mt-1 font-display font-semibold text-slate-900">{value}</p>
          </div>
        ))}
      </div>

      <div className="mt-10 rounded-2xl border border-slate-200 bg-white overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Activity className="h-4 w-4 text-emerald-800" />
            <h2 className="font-display font-semibold text-slate-900">Audit log stream</h2>
          </div>
          <span className="text-xs font-mono text-slate-500">simulated · demo</span>
        </div>
        {!user ? (
          <div className="p-6 text-sm text-slate-600">Sign in to view your audit log stream.</div>
        ) : (
          <ul className="divide-y divide-slate-100" data-testid="audit-log-list">
            {events.map((e) => (
              <li key={e.id} className="px-6 py-4 flex items-start gap-4">
                <span className={`px-2 py-0.5 rounded text-[10px] font-mono uppercase border ${SEV_STYLES[e.severity] || SEV_STYLES.info}`}>{e.severity}</span>
                <div className="flex-1">
                  <div className="font-mono text-sm text-slate-800">{e.event}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{e.detail} · {e.actor}</div>
                </div>
                <span className="text-[11px] font-mono text-slate-400">{new Date(e.at).toLocaleTimeString()}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="mt-8 rounded-2xl border border-amber-200 bg-amber-50 p-5 flex items-start gap-3">
        <ShieldAlert className="h-5 w-5 text-amber-700 shrink-0 mt-0.5" />
        <div className="text-sm text-amber-900">
          <strong>Demo notice:</strong> ViewBox AI surfaces healthcare-native patterns and hints. Real production
          use requires signed BAAs, hosting on compliant infrastructure, and formal risk assessment.
          Never store real PHI in demo accounts.
        </div>
      </div>
    </div>
  );
}
